import type { DspModuleDescriptor, DspPortDescriptor } from '../../packages/dsp-module-contract/src/index.js';
import { PatchEditor, type EditResult, type PatchViewNode } from '../../packages/patch-editor/src/index.js';
import { deserializePatch, serializePatch } from '../../packages/patch-format/src/index.js';
import type { PatchEndpoint, PatchGraphV1 } from '../../packages/routing-graph/src/index.js';

export const PATCH_STORAGE_KEY = 'allmost.patch-bay.v2';
const SVG_NS = 'http://www.w3.org/2000/svg';
const NODE_WIDTH = 250;
const WORKSPACE_WIDTH = 1_800;
const WORKSPACE_HEIGHT = 1_920;
const portY = (node: PatchViewNode, direction: 'input' | 'output', index: number): number =>
  node.y + 92 + index * 22;
const nodeHeight = (node: PatchViewNode): number => Math.max(130, 112 + Math.max(node.inputs.length, node.outputs.length) * 22);
const clampPosition = (node: PatchViewNode, x: number, y: number): { x: number; y: number } => ({
  x: Math.max(0, Math.min(WORKSPACE_WIDTH - NODE_WIDTH, x)),
  y: Math.max(0, Math.min(WORKSPACE_HEIGHT - nodeHeight(node), y)),
});

export interface PatchBayElements {
  readonly graph: SVGSVGElement;
  readonly workspace: HTMLElement;
  readonly catalog: HTMLUListElement;
  readonly contextMenu: HTMLElement;
  readonly addModule: HTMLButtonElement;
  readonly moduleStates?: ReadonlyMap<string, string>;
  readonly status: HTMLOutputElement;
  readonly reset: HTMLButtonElement;
  readonly removeCable: HTMLButtonElement;
  readonly removeNode: HTMLButtonElement;
  readonly zoomIn: HTMLButtonElement;
  readonly zoomOut: HTMLButtonElement;
  readonly exportButton: HTMLButtonElement;
  readonly importButton: HTMLButtonElement;
  readonly json: HTMLTextAreaElement;
  readonly parameterPanel?: HTMLElement;
  readonly validateParameter?: (moduleId: string, parameterId: string, value: number, nodeId: string) => void;
  readonly onChange?: (graph: PatchGraphV1) => void;
  readonly undo?: HTMLButtonElement;
  readonly redo?: HTMLButtonElement;
}

export interface PatchBay {
  readonly editor: PatchEditor;
  render(): void;
  reset(): void;
  importJson(encoded: string): EditResult;
  exportJson(): string;
  undo(): void;
  redo(): void;
}

const svgElement = <K extends keyof SVGElementTagNameMap>(name: K): SVGElementTagNameMap[K] =>
  document.createElementNS(SVG_NS, name);
const displayModuleId = (id: string): string => id.replace('mir.mod-', 'MOD-').toUpperCase();
const compactLabel = (label: string): string => label.length > 29 ? `${label.slice(0, 28)}…` : label;
const readableImportError = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error);
  return /Cannot read properties|undefined|not iterable/i.test(message)
    ? 'invalid PatchGraphV1 structure: node, port or connection data is missing'
    : message;
};

export const createPatchBay = (
  elements: PatchBayElements,
  initialGraph: PatchGraphV1,
  descriptors: readonly DspModuleDescriptor[],
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
): PatchBay => {
  let backingStorage = storage;
  if (storage === undefined) {
    try { backingStorage = globalThis.localStorage; }
    catch { backingStorage = undefined; }
  }
  let editor = new PatchEditor(initialGraph, descriptors);
  let lastEncoded = serializePatch(editor.graph);
  const undoHistory: string[] = [], redoHistory: string[] = [];
  let navigatingHistory = false;
  let pendingOutput: PatchEndpoint | undefined;
  let selectedCable: string | undefined;
  let selectedNode: string | undefined;
  let zoom = 1;
  let sequence = 0;
  let addPosition = { x: 60, y: 60 };

  const graphPosition = (clientX: number, clientY: number): { x: number; y: number } => {
    const rect = elements.graph.getBoundingClientRect();
    const viewBox = elements.graph.viewBox.baseVal;
    return {
      x: Math.max(0, Math.min(WORKSPACE_WIDTH - NODE_WIDTH, viewBox.x + (clientX - rect.left) * viewBox.width / Math.max(1, rect.width))),
      y: Math.max(0, Math.min(WORKSPACE_HEIGHT - 160, viewBox.y + (clientY - rect.top) * viewBox.height / Math.max(1, rect.height))),
    };
  };
  const closeCatalog = (): void => { elements.contextMenu.hidden = true; };
  const openCatalog = (clientX: number, clientY: number): void => {
    addPosition = graphPosition(clientX, clientY);
    elements.contextMenu.hidden = false;
    const menuWidth = Math.min(520, Math.max(240, window.innerWidth - 24));
    elements.contextMenu.style.left = `${Math.max(12, Math.min(clientX, window.innerWidth - menuWidth - 12))}px`;
    elements.contextMenu.style.top = `${Math.max(12, Math.min(clientY, window.innerHeight - 300))}px`;
    queueMicrotask(() => elements.catalog.querySelector<HTMLButtonElement>('button')?.focus());
  };

  const announce = (message: string, error = false): void => {
    elements.status.textContent = message;
    elements.status.dataset.state = error ? 'error' : 'ok';
  };
  const persist = (): boolean => {
    try { backingStorage?.setItem(PATCH_STORAGE_KEY, serializePatch(editor.graph)); return true; }
    catch { announce('Patch changed, but local persistence is unavailable.', true); return false; }
  };
  const finish = (result: EditResult, successMessage: string): boolean => {
    if (!result.ok) { announce(`Patch unchanged: ${result.error}`, true); return false; }
    const encoded = serializePatch(editor.graph);
    if (encoded !== lastEncoded && !navigatingHistory) { undoHistory.push(lastEncoded); if (undoHistory.length > 50) undoHistory.shift(); redoHistory.length = 0; }
    lastEncoded = encoded;
    render();
    if (persist()) announce(successMessage);
    elements.onChange?.(editor.graph);
    return true;
  };
  const navigateHistory = (undo: boolean): void => {
    const from = undo ? undoHistory : redoHistory, to = undo ? redoHistory : undoHistory;
    const encoded = from.at(-1); if (!encoded) return;
    const previous = serializePatch(editor.graph);
    const result = editor.replaceGraph(deserializePatch(encoded)); if (!result.ok) { announce(result.error ?? 'History rejected', true); return; }
    from.pop(); to.push(previous); navigatingHistory = true;
    try { finish(result, undo ? 'Undo applied to visible graph.' : 'Redo applied to visible graph.'); } finally { navigatingHistory = false; }
  };
  elements.undo?.addEventListener('click', () => navigateHistory(true));
  elements.redo?.addEventListener('click', () => navigateHistory(false));
  const endpoint = (node: PatchViewNode, direction: 'input' | 'output', port: DspPortDescriptor, index: number): SVGCircleElement => {
    const circle = svgElement('circle');
    circle.setAttribute('cx', String(direction === 'input' ? 0 : NODE_WIDTH));
    circle.setAttribute('cy', String(92 + index * 22));
    circle.setAttribute('r', '7');
    circle.classList.add('patch-port', `port-${port.kind}`);
    circle.dataset.nodeId = node.id;
    circle.dataset.portId = port.id;
    circle.dataset.direction = direction;
    circle.setAttribute('role', 'button');
    circle.setAttribute('tabindex', '0');
    circle.setAttribute('aria-label', `${direction} ${port.id}, ${port.kind}, ${port.channels} channel`);
    const activate = (event: Event): void => {
      event.stopPropagation();
      if (direction === 'output') {
        pendingOutput = { nodeId: node.id, portId: port.id };
        announce(`Selected ${node.id}.${port.id}; choose an input.`);
        render();
        return;
      }
      if (!pendingOutput) { announce('Choose an output before choosing an input.', true); return; }
      const source = pendingOutput;
      pendingOutput = undefined;
      const id = `cable-${Date.now().toString(36)}-${sequence++}`;
      finish(editor.connect({ id, source, target: { nodeId: node.id, portId: port.id } }), `Connected ${source.nodeId}.${source.portId} to ${node.id}.${port.id}.`);
    };
    circle.addEventListener('click', activate);
    circle.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(event); }
    });
    return circle;
  };

  const render = (): void => {
    if (elements.undo) elements.undo.disabled = undoHistory.length === 0;
    if (elements.redo) elements.redo.disabled = redoHistory.length === 0;
    elements.graph.replaceChildren();
    const view = editor.getView();
    const nodes = new Map(view.nodes.map((node) => [node.id, node]));
    for (const cable of view.cables) {
      const source = nodes.get(cable.source.nodeId)!;
      const target = nodes.get(cable.target.nodeId)!;
      const sourceIndex = source.outputs.findIndex(({ id }) => id === cable.source.portId);
      const targetIndex = target.inputs.findIndex(({ id }) => id === cable.target.portId);
      const sx = source.x + NODE_WIDTH;
      const sy = portY(source, 'output', sourceIndex);
      const tx = target.x;
      const ty = portY(target, 'input', targetIndex);
      const path = svgElement('path');
      path.setAttribute('d', `M ${sx} ${sy} C ${sx + 65} ${sy}, ${tx - 65} ${ty}, ${tx} ${ty}`);
      path.classList.add('cable', `cable-${cable.kind}`);
      if (selectedCable === cable.id) path.classList.add('selected');
      path.setAttribute('tabindex', '0');
      path.setAttribute('role', 'button');
      path.setAttribute('aria-label', `${cable.kind} cable ${cable.source.nodeId}.${cable.source.portId} to ${cable.target.nodeId}.${cable.target.portId}`);
      const select = (restoreFocus = false): void => {
        selectedCable = cable.id; selectedNode = undefined; announce(`Selected cable ${cable.id}.`); render();
        if (restoreFocus) queueMicrotask(() => [...elements.graph.querySelectorAll<SVGPathElement>('.cable')]
          .find((candidate) => candidate.getAttribute('aria-label') === path.getAttribute('aria-label'))?.focus());
      };
      path.addEventListener('click', () => { select(); });
      path.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); select(true); }
        if (event.key === 'Delete' || event.key === 'Backspace') { event.preventDefault(); selectedCable = cable.id; elements.removeCable.click(); }
      });
      elements.graph.append(path);
    }
    for (const node of view.nodes) {
      const group = svgElement('g');
      group.classList.add('patch-node');
      const runtimeState = node.id === node.moduleId.replace('mir.', '')
        ? elements.moduleStates?.get(node.moduleId)
        : undefined;
      if (runtimeState) group.classList.add(`runtime-${runtimeState}`);
      if (selectedNode === node.id) group.classList.add('selected');
      group.setAttribute('transform', `translate(${node.x} ${node.y})`);
      group.dataset.nodeId = node.id;
      const height = nodeHeight(node);
      const rect = svgElement('rect');
      rect.setAttribute('width', String(NODE_WIDTH)); rect.setAttribute('height', String(height)); rect.setAttribute('rx', '14');
      group.append(rect);
      const tooltip = svgElement('title'); tooltip.textContent = `${node.label} · ${node.moduleId} · ${node.id}`; group.append(tooltip);
      const title = svgElement('text'); title.setAttribute('x', '16'); title.setAttribute('y', '30'); title.classList.add('title'); title.textContent = compactLabel(node.label); group.append(title);
      const moduleId = svgElement('text'); moduleId.setAttribute('x', '16'); moduleId.setAttribute('y', '54'); moduleId.textContent = `${displayModuleId(node.moduleId)} · ${node.id}`; group.append(moduleId);
      if (runtimeState) {
        const state = svgElement('text'); state.setAttribute('x', '16'); state.setAttribute('y', '75'); state.classList.add('runtime-state'); state.textContent = runtimeState.toUpperCase(); group.append(state);
      }
      node.inputs.forEach((port, index) => {
        group.append(endpoint(node, 'input', port, index));
        const label = svgElement('text'); label.setAttribute('x', '14'); label.setAttribute('y', String(97 + index * 22)); label.textContent = `${port.id} · ${port.kind}`; group.append(label);
      });
      node.outputs.forEach((port, index) => {
        group.append(endpoint(node, 'output', port, index));
        const label = svgElement('text'); label.setAttribute('x', String(NODE_WIDTH - 14)); label.setAttribute('text-anchor', 'end'); label.setAttribute('y', String(97 + index * 22)); label.textContent = `${port.id} · ${port.kind}`; group.append(label);
      });
      group.setAttribute('tabindex', '0');
      group.setAttribute('role', 'button');
      group.setAttribute('aria-pressed', selectedNode === node.id ? 'true' : 'false');
      group.setAttribute('aria-label', `${node.label}, ${displayModuleId(node.moduleId)}, patch module ${node.id}`);
      const focusNode = (): void => queueMicrotask(() => [...elements.graph.querySelectorAll<SVGGElement>('.patch-node')]
        .find((candidate) => candidate.dataset.nodeId === node.id)?.focus());
      const selectNode = (restoreFocus = false): void => {
        selectedNode = node.id; selectedCable = undefined; announce(`Selected module ${node.id}.`); render();
        if (restoreFocus) focusNode();
      };
      group.addEventListener('click', () => { selectNode(); });
      group.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); selectNode(true); return; }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault(); selectedNode = node.id; elements.removeNode.click(); return;
        }
        const step = event.shiftKey ? 5 : 20;
        const delta = event.key === 'ArrowLeft' ? [-step, 0] : event.key === 'ArrowRight' ? [step, 0]
          : event.key === 'ArrowUp' ? [0, -step] : event.key === 'ArrowDown' ? [0, step] : undefined;
        if (!delta) return;
        event.preventDefault();
        finish(editor.moveNode(node.id, clampPosition(node, node.x + delta[0], node.y + delta[1])), `Moved ${node.id}.`);
        focusNode();
      });
      group.addEventListener('pointerdown', (event) => {
        if ((event.target as Element).classList.contains('patch-port')) return;
        event.preventDefault();
        group.setPointerCapture(event.pointerId);
        const start = { x: event.clientX, y: event.clientY, nodeX: node.x, nodeY: node.y };
        const move = (moveEvent: PointerEvent): void => {
          const scale = zoom;
          group.setAttribute('transform', `translate(${start.nodeX + (moveEvent.clientX - start.x) / scale} ${start.nodeY + (moveEvent.clientY - start.y) / scale})`);
        };
        const up = (upEvent: PointerEvent): void => {
          group.removeEventListener('pointermove', move);
          group.removeEventListener('pointerup', up);
          const position = clampPosition(node, start.nodeX + (upEvent.clientX - start.x) / zoom, start.nodeY + (upEvent.clientY - start.y) / zoom);
          // render() replaces this SVG group before the browser dispatches click.
          // Select here too, so a real pointer tap opens parameters instead of
          // relying on a synthetic click that only test DOMs delivered.
          selectedNode = node.id; selectedCable = undefined;
          finish(editor.moveNode(node.id, position), `Selected / moved ${node.id}.`);
        };
        group.addEventListener('pointermove', move);
        group.addEventListener('pointerup', up);
      });
      elements.graph.append(group);
    }
    elements.graph.setAttribute('viewBox', `0 0 ${WORKSPACE_WIDTH / zoom} ${WORKSPACE_HEIGHT / zoom}`);
    elements.removeCable.disabled = !selectedCable;
    elements.removeNode.disabled = !selectedNode;
    elements.json.value = serializePatch(editor.graph);
    if (elements.parameterPanel) {
      const panel = elements.parameterPanel;
      panel.replaceChildren();
      const legend = document.createElement('legend');
      legend.textContent = selectedNode ? `Parametre: ${selectedNode}` : 'Vyberte modul pre parametre';
      panel.append(legend);
      const node = editor.graph.nodes.find(n => n.id === selectedNode);
      const descriptor = descriptors.find(d => d.id === node?.moduleId);
      if (node && descriptor) for (const parameter of descriptor.parameters) {
        const label = document.createElement('label'); label.textContent = `${parameter.id} (${parameter.unit}) `;
        const input = document.createElement('input'); input.type = 'number'; input.step = 'any';
        input.min = String(parameter.minValue); input.max = String(parameter.maxValue);
        input.value = String(node.parameters[parameter.id] ?? parameter.defaultValue);
        input.setAttribute('aria-label', `${node.id} ${parameter.id}`);
        input.addEventListener('change', () => {
          if (!input.checkValidity() || !Number.isFinite(input.valueAsNumber)) { announce('Parameter outside descriptor range.', true); return; }
          try { elements.validateParameter?.(node.moduleId, parameter.id, input.valueAsNumber, node.id); }
          catch (error) { input.value = String(node.parameters[parameter.id] ?? parameter.defaultValue); announce(`Parameter unchanged: ${error instanceof Error ? error.message : String(error)}`, true); return; }
          const next = { ...editor.graph, nodes: editor.graph.nodes.map(n => n.id === node.id ? { ...n, parameters: { ...n.parameters, [parameter.id]: input.valueAsNumber } } : n) };
          finish(editor.replaceGraph(next), `${node.id}.${parameter.id} saved; live audition follows edits when active.`);
        });
        label.append(input); panel.append(label);
      }
    }
  };

  const reset = (): void => {
    pendingOutput = undefined; selectedCable = undefined; selectedNode = undefined;
    finish(editor.replaceGraph(initialGraph), 'Reference patch restored.');
  };
  const importJson = (encoded: string): EditResult => {
    try {
      const result = editor.replaceGraph(deserializePatch(encoded));
      finish(result, 'Imported canonical PatchGraphV1.');
      return result;
    } catch (error) {
      const result: EditResult = { ok: false, error: readableImportError(error) };
      announce(`Patch unchanged: ${result.error}`, true);
      return result;
    }
  };
  const exportJson = (): string => serializePatch(editor.graph);

  for (const descriptor of descriptors) {
    const item = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.innerHTML = `<strong>${displayModuleId(descriptor.id)}</strong><span>${descriptor.name}</span>`;
    button.setAttribute('aria-label', `Add ${displayModuleId(descriptor.id)} ${descriptor.name}`);
    button.addEventListener('click', () => {
      let number = 1;
      const base = descriptor.id.replace('mir.', '');
      while (editor.graph.nodes.some(({ id }) => id === `${base}-${number}`)) number += 1;
      finish(editor.addNode({ id: `${base}-${number}`, moduleId: descriptor.id, parameters: {}, position: addPosition }), `Added ${descriptor.name}.`);
      closeCatalog();
    });
    item.append(button); elements.catalog.append(item);
  }
  elements.addModule.addEventListener('click', () => {
    const rect = elements.workspace.getBoundingClientRect();
    openCatalog(rect.left + rect.width / 2, rect.top + Math.min(rect.height / 2, 240));
  });
  elements.workspace.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    openCatalog(event.clientX, event.clientY);
  });
  elements.reset.addEventListener('click', reset);
  elements.removeCable.addEventListener('click', () => {
    if (!selectedCable) return;
    const id = selectedCable;
    const result = editor.disconnect(id);
    if (result.ok) selectedCable = undefined;
    finish(result, `Removed cable ${id}.`);
  });
  elements.removeNode.addEventListener('click', () => {
    if (!selectedNode) return;
    const id = selectedNode;
    const result = editor.removeNode(id);
    if (result.ok) selectedNode = undefined;
    finish(result, `Removed module ${id}.`);
  });
  elements.zoomIn.addEventListener('click', () => { zoom = Math.min(2, zoom + 0.25); render(); announce(`Viewport zoom ${Math.round(zoom * 100)}%.`); });
  elements.zoomOut.addEventListener('click', () => { zoom = Math.max(0.5, zoom - 0.25); render(); announce(`Viewport zoom ${Math.round(zoom * 100)}%.`); });
  elements.exportButton.addEventListener('click', () => { elements.json.value = exportJson(); elements.json.select(); announce('Canonical PatchGraphV1 ready to copy.'); });
  elements.importButton.addEventListener('click', () => { importJson(elements.json.value); });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !elements.contextMenu.hidden) {
      event.preventDefault();
      event.stopPropagation();
      closeCatalog();
      elements.graph.focus();
      return;
    }
    if (event.key === 'Escape' && pendingOutput) { pendingOutput = undefined; announce('Connection cancelled.'); render(); }
    if ((event.key === 'Delete' || event.key === 'Backspace') && document.activeElement === elements.graph) {
      if (selectedCable) elements.removeCable.click();
      else if (selectedNode) elements.removeNode.click();
    }
  });
  document.addEventListener('pointerdown', (event) => {
    if (!elements.contextMenu.hidden && !elements.contextMenu.contains(event.target as Node) && event.target !== elements.addModule) closeCatalog();
  });

  try {
    const saved = backingStorage?.getItem(PATCH_STORAGE_KEY);
    if (saved) {
      const restored = editor.replaceGraph(deserializePatch(saved));
      if (!restored.ok) throw new Error(restored.error);
      announce('Restored local PatchGraphV1.');
    } else announce(`Reference PatchGraph ready · ${editor.graph.nodes.length} machines · ${editor.graph.connections.length} valid cables. Select an output, then an input.`);
  } catch (error) {
    try { backingStorage?.removeItem(PATCH_STORAGE_KEY); } catch { /* unavailable storage stays disabled */ }
    announce(`Saved patch ignored: ${error instanceof Error ? error.message : String(error)}`, true);
  }
  lastEncoded = serializePatch(editor.graph);
  render();

  return { get editor() { return editor; }, render, reset, importJson, exportJson, undo: () => navigateHistory(true), redo: () => navigateHistory(false) };
};
